import React, { useState, useEffect } from "react";
import "./teamManagement.css";
import API_Root from "../../Constants/API_Root";
import { useDispatch } from "react-redux";
import { toggleToast } from "../../Actions/ToggleToast";
import { authFetch } from "../../Authentication/authService";

const TeamManagement = ({
  userEmail,
  switchTeams,
  currentTeamName,
}) => {
  const dispatch = useDispatch();
  const [teamName, setTeamName] = useState(currentTeamName)

  const [teams, setTeams] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  useEffect(() => {
    if (switchTeams && switchTeams.length > 0) {
      const formatted = switchTeams.map((team) => ({
        id: team.id,
        name: team.team_name,
        members: [],
      }));
      setTeams(formatted);
    }
  }, [switchTeams]);

  const [activeTeamId, setActiveTeamId] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editTeamId, setEditTeamId] = useState(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("User");
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);

  const activeTeam = teams.find((t) => t.id === activeTeamId);

  // ✅ Handle Team Change
  const handleTeamChange = (teamId) => {
    setActiveTeamId(teamId);
    setSelectedTeam(null);
    setEditTeamId(null);
    setShowAddMemberForm(false);
  };

  // ✅ Invite Click → API Call
  const handleInviteClick = (team) => {
    setSelectedTeam(team.id);
    setLoadingMembers(true);

    authFetch(`${API_Root}team/get-team-members-by-name`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        team_name: team.name,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch members");
        return res.json();
      })
      .then((data) => {
        const apiMembers = data?.members || [];

        const formattedMembers = apiMembers.map((m, index) => ({
          id: index + 1,
          email: m.email,
          role: m.role,
        }));

        setTeams((prev) =>
          prev.map((t) =>
            t.id === team.id ? { ...t, members: formattedMembers } : t
          )
        );
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          toggleToast({
            show: true,
            type: "error",
            message: "Failed to load team members",
          })
        );
      })
      .finally(() => {
        setLoadingMembers(false);
      });
  };

  const handleEditTeamName = () => {
    authFetch(`${API_Root}team/rename-team-name`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ old_team_name: currentTeamName, new_team_name: teamName }),
    })
    .then((response) => response.json())
    .then((data) => {
      if (data.error) throw new Error(data.error);

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: `${data.message}`,
        })
      );

      setTeamName(teamName);
    });
  }

  // ➕ Create Team
  const handleCreateTeam = () => {
    authFetch(`${API_Root}team/create-demo-team`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: userEmail, team_name: newTeamName }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);

        dispatch(
          toggleToast({
            show: true,
            type: "success",
            message: `${data.message}`,
          })
        );

        setNewTeamName("");
      });
  };

  const handleAddMember = async (teamId, teamName) => {
    if (!inviteEmail) return;

    const payload = {
      team_name: teamName,
      invites: [
        {
          email: inviteEmail,
          role: inviteRole,
        },
      ],
    };

    try {
      const res = await authFetch(API_Root + "team/invite-team-members", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data?.message || "Invite failed");

      // ✅ Success Toast
      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "Member invited successfully",
        })
      );

      // ✅ OPTIONAL: update UI instantly
      setTeams((prev) =>
        prev.map((team) =>
          team.id === teamId
            ? {
                ...team,
                members: [
                  ...team.members,
                  {
                    id: Date.now(),
                    email: inviteEmail,
                    role: inviteRole,
                  },
                ],
              }
            : team
        )
      );

      // reset form
      setInviteEmail("");
      setInviteRole("User");
      setShowAddMemberForm(false);
    } catch (err) {
      console.error(err);

      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message || "Failed to invite member",
        })
      );
    }
  };

  const handleEditClick = (team) => {
    setEditTeamId(team.id);
    setSelectedTeam(null);
    setLoadingMembers(true);

    authFetch(`${API_Root}team/get-team-members-by-name`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        team_name: team.name,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch members");
        return res.json();
      })
      .then((data) => {
        const apiMembers = data?.members || [];

        const formattedMembers = apiMembers.map((m, index) => ({
          id: index + 1,
          email: m.email,
          role: m.role,
        }));

        setTeams((prev) =>
          prev.map((t) =>
            t.id === team.id ? { ...t, members: formattedMembers } : t
          )
        );
      })
      .catch(() => {
        dispatch(
          toggleToast({
            show: true,
            type: "error",
            message: "Failed to load team members",
          })
        );
      })
      .finally(() => setLoadingMembers(false));
  };

  const handleRemoveMemberAPI = async (teamName, teamId, email, memberId) => {
    try {
      const response = await authFetch(API_Root + "team/remove-team-member", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          team_name: teamName,
          email,
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data?.message || "Remove failed");

      // ✅ Toast
      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "Member removed successfully",
        })
      );

      // ✅ Update UI
      setTeams((prev) =>
        prev.map((team) =>
          team.id === teamId
            ? {
                ...team,
                members: team.members.filter((m) => m.id !== memberId),
              }
            : team
        )
      );
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message || "Failed to remove member",
        })
      );
    }
  };

  // 🔁 Change Role
  const handleRoleChange = (teamId, memberId, role) => {
    setTeams(
      teams.map((team) =>
        team.id === teamId
          ? {
              ...team,
              members: team.members.map((m) =>
                m.id === memberId ? { ...m, role } : m
              ),
            }
          : team
      )
    );
  };

  // ❌ Remove Member
  const handleRemoveMember = (teamId, memberId) => {
    setTeams(
      teams.map((team) =>
        team.id === teamId
          ? {
              ...team,
              members: team.members.filter((m) => m.id !== memberId),
            }
          : team
      )
    );
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    try {
      const res = await authFetch(`${API_Root}team/delete-demo-team`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          team_name: teamName,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data?.message || "Delete failed");

      // ✅ Toast
      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "Team deleted successfully",
        })
      );

      // ✅ Remove from UI
      setTeams((prev) => prev.filter((t) => t.id !== teamId));

      // ✅ Reset selection if deleted team was active
      if (activeTeamId === teamId) {
        setActiveTeamId(null);
      }

      // ✅ Reset panels
      setSelectedTeam(null);
      setEditTeamId(null);
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message || "Failed to delete team",
        })
      );
    }
  };

  return (
    <div className="tm-wrapper">
      {/* Header */}
      <div className="tm-top">
        <div>
          <h2>{activeTeamId ? activeTeam?.name : "All Teams"}</h2>
          <p className="tm-sub">Manage your teams and members</p>
        </div>

        <select
          className="tm-select"
          value={activeTeamId || ""}
          onChange={(e) =>
            handleTeamChange(
              e.target.value === "" ? null : Number(e.target.value)
            )
          }
        >
          <option value="">All Team</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{width: '350px', paddingLeft: '18px'}}>
        <h4>Edit Team Name</h4>
        <div className="tm-row">
          <input
            placeholder="Enter team name"
            style={{width: '40px'}}
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
          />
          <button onClick={handleEditTeamName}>Create</button>
        </div>
      </div>

      {/* Create Team */}
      <div className="tm-card">
        <h4>Create Team</h4>
        <div className="tm-row">
          <input
            placeholder="Enter team name"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
          />
          <button onClick={handleCreateTeam}>Create</button>
        </div>
      </div>

      {/* TEAM LIST */}
      <div className="tm-card">
        <h4>All Teams</h4>

        {teams
          .filter((team) => (activeTeamId ? team.id === activeTeamId : true))
          .map((team) => (
            <div key={team.id} className="tm-team-card">
              {/* HEADER */}
              <div className="tm-team-header">
                <span>{team.name}</span>

                <div className="tm-actions">
                  <button onClick={() => handleInviteClick(team)}>
                    Invite
                  </button>

                  <button onClick={() => handleEditClick(team)}>Edit</button>

                  <button
                    className="danger"
                    onClick={() => handleDeleteTeam(team.id, team.name)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* INVITE PANEL */}
              {selectedTeam === team.id && (
                <div className="tm-expand">
                  <h5>Members</h5>

                  {loadingMembers ? (
                    <p className="empty">Loading members...</p>
                  ) : team.members.length === 0 ? (
                    <p className="empty">No members found</p>
                  ) : null}

                  {team.members.map((member) => (
                    <div key={member.id} className="tm-member-row">
                      <span>{member.email}</span>

                      <select
                        value={member.role}
                        onChange={(e) =>
                          handleRoleChange(team.id, member.id, e.target.value)
                        }
                      >
                        <option>User</option>
                        <option>Admin</option>
                      </select>

                      <input type="checkbox" />
                    </div>
                  ))}

                  <div className="tm-action-row">
                    <button
                      className="primary"
                      onClick={() => handleAddMember(team.id, team.name)}
                    >
                      Assign Member
                    </button>

                    <button
                      className="success"
                      onClick={() => setShowAddMemberForm(true)}
                    >
                      Add Member
                    </button>

                    <button
                      onClick={() => {
                        setSelectedTeam(null);
                        setShowAddMemberForm(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>

                  {showAddMemberForm && (
                    <div className="tm-sub-card">
                      <h5>Add Member</h5>

                      <input
                        type="email"
                        placeholder="Enter email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />

                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                      >
                        <option>User</option>
                        <option>Admin</option>
                      </select>

                      <div className="tm-action-row">
                        <button
                          className="primary"
                          onClick={() => handleAddMember(team.id, team.name)}
                        >
                          Invite
                        </button>

                        <button onClick={() => setShowAddMemberForm(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* EDIT PANEL */}
              {editTeamId === team.id && (
                <div className="tm-expand edit">
                  <h5>Edit Members</h5>

                  {team.members.map((member) => (
                    <div key={member.id} className="tm-member-row">
                      <span>{member.email}</span>

                      <span className="badge">{member.role}</span>

                      <button
                        className="danger small"
                        onClick={() =>
                          handleRemoveMemberAPI(
                            team.name,
                            team.id,
                            member.email,
                            member.id
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  <button onClick={() => setEditTeamId(null)}>Close</button>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default TeamManagement;
